/**
 * Modal para ver el estado de cuenta de un miembro
 */

import { useEffect, useMemo } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Button,
} from "@/components/ui";
import {
  X,
  Loader2,
  User,
  DollarSign,
  Receipt,
  CheckCircle,
  Clock,
  AlertCircle,
  Download,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import { useChargesByMember, usePaymentsByMember } from "@/hooks/useFinance";
import { usePermissions } from "@/hooks/usePermissions";
import { toJsDate } from "@/lib/services/activitySubmissions";
import { formatCurrency } from "@/lib/utils";
import type { MemberLegacy as Member, ChargeStatus } from "@/types";
import { exportToXlsx } from "@/lib/utils/exportXlsx";

interface MemberStatementModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: Member;
}

const statusLabels: Record<ChargeStatus, string> = {
  pending: "Pendiente",
  partial: "Parcial",
  paid: "Pagado",
  overdue: "Vencido",
  cancelled: "Cancelado",
};

const statusColors: Record<ChargeStatus, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  partial: "bg-orange-100 text-orange-800",
  paid: "bg-green-100 text-green-800",
  overdue: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-800",
};

export default function MemberStatementModal({
  isOpen,
  onClose,
  member,
}: MemberStatementModalProps) {
  const { canSeeMember } = usePermissions();
  const allowed = canSeeMember({
    clubId: member.clubId,
    section: member.section,
    staffSection: member.staffSection,
  });

  // Auto-cerrar si no tiene acceso (defensa en profundidad ante deep-links
  // o cambios de permisos en vivo).
  useEffect(() => {
    if (isOpen && !allowed) {
      onClose();
    }
  }, [isOpen, allowed, onClose]);

  const { data: charges, isLoading: loadingCharges } = useChargesByMember(
    allowed ? member.id : "",
  );
  const { data: payments, isLoading: loadingPayments } = usePaymentsByMember(
    allowed ? member.id : "",
  );

  const isLoading = loadingCharges || loadingPayments;

  // Calcular resumen
  const summary = useMemo(() => {
    const totalCharged = charges?.reduce((sum, c) => sum + c.amount, 0) || 0;
    const totalPaid = payments?.reduce((sum, p) => sum + p.amount, 0) || 0;
    const balance = totalCharged - totalPaid;
    const pendingCount =
      charges?.filter((c) => c.status === "pending" || c.status === "partial")
        .length || 0;
    const overdueCount =
      charges?.filter((c) => c.status === "overdue").length || 0;

    return {
      totalCharged,
      totalPaid,
      balance,
      pendingCount,
      overdueCount,
    };
  }, [charges, payments]);

  // Combinar y ordenar transacciones
  const transactions = useMemo(() => {
    const chargeTransactions = (charges || []).map((c) => ({
      id: c.id,
      type: "charge" as const,
      date: c.createdAt,
      concept: c.conceptName,
      description: c.description || "",
      amount: c.amount,
      balance: c.balance,
      status: c.status,
    }));

    const paymentTransactions = (payments || []).map((p) => ({
      id: p.id,
      type: "payment" as const,
      date: p.createdAt,
      concept: "Pago recibido",
      description: p.reference || p.paymentMethod,
      amount: p.amount,
      balance: 0,
      status: p.status,
    }));

    return [...chargeTransactions, ...paymentTransactions].sort((a, b) => {
      const dateA = toJsDate(a.date) ?? new Date(0);
      const dateB = toJsDate(b.date) ?? new Date(0);
      return dateB.getTime() - dateA.getTime();
    });
  }, [charges, payments]);

  const formatDate = (timestamp: Parameters<typeof toJsDate>[0]) => {
    const d = toJsDate(timestamp);
    if (!d) return "-";
    return d.toLocaleDateString("es-CO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const exportToCSV = () => {
    const memberName =
      member.fullName || `${member.firstName} ${member.lastName}`;
    exportToXlsx({
      filename: `estado-cuenta-${memberName.replace(/\s+/g, "-")}-${new Date().toISOString().split("T")[0]}`,
      sheetName: "Estado de cuenta",
      rows: transactions,
      columns: [
        { header: "Fecha", key: (t) => formatDate(t.date), width: 14 },
        {
          header: "Tipo",
          key: (t) => (t.type === "charge" ? "Cobro" : "Pago"),
          width: 10,
        },
        { header: "Concepto", key: (t) => t.concept, width: 24 },
        { header: "Descripción", key: (t) => t.description, width: 32 },
        { header: "Monto", key: (t) => t.amount, width: 14 },
        {
          header: "Saldo",
          key: (t) => (t.type === "charge" ? t.balance : ""),
          width: 14,
        },
      ],
    });
  };

  if (!isOpen) return null;
  if (!allowed) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />

        <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-md shadow-blue-500/25">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">{member.fullName}</h2>
                <p className="text-sm text-gray-500">Estado de Cuenta</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={exportToCSV}>
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-4 max-h-[70vh] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Resumen */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-0 shadow-md">
                    <CardContent className="p-4 text-center">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-sm shadow-blue-500/25">
                        <TrendingDown className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-3xl font-bold text-blue-700">
                        {formatCurrency(summary.totalCharged)}
                      </p>
                      <p className="text-xs font-medium text-blue-600">
                        Total Cargado
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-green-50 to-green-100/50 border-0 shadow-md">
                    <CardContent className="p-4 text-center">
                      <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-sm shadow-green-500/25">
                        <TrendingUp className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-3xl font-bold text-green-700">
                        {formatCurrency(summary.totalPaid)}
                      </p>
                      <p className="text-xs font-medium text-green-600">
                        Total Pagado
                      </p>
                    </CardContent>
                  </Card>

                  <Card
                    className={`${summary.balance > 0 ? "bg-red-50 border-red-100" : "bg-gray-50 border-gray-100"}`}
                  >
                    <CardContent className="p-4 text-center">
                      <DollarSign
                        className={`w-6 h-6 mx-auto mb-1 ${summary.balance > 0 ? "text-red-600" : "text-gray-600"}`}
                      />
                      <p
                        className={`text-3xl font-bold ${summary.balance > 0 ? "text-red-700" : "text-gray-700"}`}
                      >
                        {formatCurrency(summary.balance)}
                      </p>
                      <p
                        className={`text-xs ${summary.balance > 0 ? "text-red-600" : "text-gray-600"}`}
                      >
                        Saldo Pendiente
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100/50 border-0 shadow-md">
                    <CardContent className="p-4 text-center">
                      <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-sm shadow-yellow-500/25">
                        <Clock className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-3xl font-bold text-yellow-700">
                        {summary.pendingCount}
                      </p>
                      <p className="text-xs font-medium text-yellow-600">
                        Cobros Pendientes
                      </p>
                      {summary.overdueCount > 0 && (
                        <p className="text-xs text-red-600 mt-1">
                          ({summary.overdueCount} vencidos)
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Cobros pendientes */}
                {charges &&
                  charges.filter(
                    (c) => c.status !== "paid" && c.status !== "cancelled",
                  ).length > 0 && (
                    <Card className="border-0 shadow-lg">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-yellow-500" />
                          Cobros Pendientes
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {charges
                            .filter(
                              (c) =>
                                c.status !== "paid" && c.status !== "cancelled",
                            )
                            .map((charge) => (
                              <div
                                key={charge.id}
                                className="flex items-center justify-between p-3 bg-yellow-50 rounded-xl"
                              >
                                <div>
                                  <p className="font-medium">
                                    {charge.conceptName}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    {charge.description}
                                    {charge.dueDate && (
                                      <span className="ml-2">
                                        • Vence: {formatDate(charge.dueDate)}
                                      </span>
                                    )}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold text-lg">
                                    {formatCurrency(charge.balance)}
                                  </p>
                                  <Badge
                                    className={statusColors[charge.status]}
                                    size="sm"
                                  >
                                    {statusLabels[charge.status]}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                {/* Historial de transacciones */}
                <Card className="border-0 shadow-lg">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">
                      Historial de Transacciones
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {transactions.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">
                        No hay transacciones registradas
                      </p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Fecha
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Tipo
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Concepto
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                Monto
                              </th>
                              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                                Estado
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {transactions.map((transaction) => (
                              <tr key={`${transaction.type}-${transaction.id}`}>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                  {formatDate(transaction.date)}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {transaction.type === "charge" ? (
                                    <span className="inline-flex items-center text-red-600 text-sm">
                                      <Receipt className="w-4 h-4 mr-1" />
                                      Cobro
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center text-green-600 text-sm">
                                      <CheckCircle className="w-4 h-4 mr-1" />
                                      Pago
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  <p className="text-sm font-medium text-gray-900">
                                    {transaction.concept}
                                  </p>
                                  {transaction.description && (
                                    <p className="text-xs text-gray-500">
                                      {transaction.description}
                                    </p>
                                  )}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-right">
                                  <span
                                    className={`font-medium ${
                                      transaction.type === "charge"
                                        ? "text-red-600"
                                        : "text-green-600"
                                    }`}
                                  >
                                    {transaction.type === "charge" ? "-" : "+"}
                                    {formatCurrency(transaction.amount)}
                                  </span>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-center">
                                  {transaction.type === "charge" ? (
                                    <Badge
                                      className={
                                        statusColors[
                                          transaction.status as ChargeStatus
                                        ]
                                      }
                                      size="sm"
                                    >
                                      {
                                        statusLabels[
                                          transaction.status as ChargeStatus
                                        ]
                                      }
                                    </Badge>
                                  ) : (
                                    <Badge
                                      className={
                                        transaction.status === "valid"
                                          ? "bg-green-100 text-green-800"
                                          : "bg-red-100 text-red-800"
                                      }
                                      size="sm"
                                    >
                                      {transaction.status === "valid"
                                        ? "Válido"
                                        : "Reversado"}
                                    </Badge>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
